/* eslint-disable no-console */
import chalk from 'chalk';
import * as _ from 'lodash';
import * as utils from '../utils';
import * as constants from '../commons/constants';
import { featureAvailabilityService } from '../commons/featureAvailabilityService';
import {
    getAbortedTests,
    getFailedTests,
    getPassedTests,
    getFailureEvaluatingCount,
    getSkippedCount,
} from './reporterUtils';
import type { IReporter } from './IReporter';
import type { RunnerOptions } from '../runOptions';
import type { ExecutionList } from '../runners/TestPlanRunner';
import type { TestRunStatusTestResult } from '../testRunStatus';
import type { TestWithCustomTestDataAndRunConfig } from 'services/src/commons/testListBuilder';

const colorize = { success: chalk.green, warn: chalk.yellow, error: chalk.red };
const { CLI_MODE } = constants;
const DEVICE = 'device';

class ConsoleReporter implements IReporter {
    private readonly config;
    constructor(private readonly options: RunnerOptions, private readonly branchToUse: string) {
        this.config = {
            showWorkerNames: options.parallel > 1, // we show worker names when there is more than one worker
        };
    }

    private toWorkerIdPrefix(workerId: number) {
        return this.config.showWorkerNames ? `W:${workerId}` : '';
    }

    private printWorkerDivider() {
        console.log('-'.repeat(process.stdout.columns || 83));
    }

    public onTestStarted(test: TestRunStatusTestResult, workerId: number) {
        const testIdLabel = `(${test.testId})`;
        const testUrlLabel = `url: ${chalk.underline(utils.getTestUrl(this.options.editorUrl!, this.options.project, test.testId, test.resultId, this.branchToUse))}`;
        console.log(this.toWorkerIdPrefix(workerId), `Test "${test.name}" started ${testIdLabel} ${testUrlLabel}`.trim());
    }

    public onTestFinished(test: TestRunStatusTestResult, workerId: number) {
        const testStatus = test.success ? constants.runnerTestStatus.PASSED : constants.runnerTestStatus.FAILED;
        const testIdLabel = `(${test.testId})`;
        const color = colorize[test.success ? 'success' : 'error'];

        console.log(color(this.toWorkerIdPrefix(workerId), `Test "${test.name}" finished status: ${testStatus} ${testIdLabel} duration: ${utils.getDuration(test.duration)}`));
    }

    private printAllFailedTests(failedTests: TestRunStatusTestResult[]) {
        if (!failedTests.length) {
            return;
        }
        const failedTestStrings = failedTests.map(failedTest => {
            const testUrl = utils.getTestUrl(this.options.editorUrl!, this.options.project, failedTest.testId, failedTest.resultId, this.branchToUse);
            return `${failedTest.name} : ${testUrl}`;
        });
        console.log(colorize.error('Failed runs are:'));
        console.log(colorize.error(failedTestStrings.join('\n\r')));
    }

    public onTestPlanFinished(testResults: { [resultId: string]: TestRunStatusTestResult }, testPlanName: string, duration: number, executionId: string, isAnonymous: boolean) {
        const failedTests = getFailedTests(testResults);
        const passedTests = getPassedTests(testResults);
        const abortedTests = getAbortedTests(testResults);

        const passed = passedTests.length;
        const aborted = abortedTests.length;
        let failed = failedTests.length;
        let failedEvaluatingString = '';
        let skippedString = '';
        if (featureAvailabilityService.isTestStatusEnabled) {
            const failureEvaluatingCount = getFailureEvaluatingCount(testResults);
            failedEvaluatingString = ` FAILED-EVALUATING: ${failureEvaluatingCount}`;
            failed -= failureEvaluatingCount;

            const skippedCount = getSkippedCount(testResults, this.options);
            skippedString = ` SKIPPED: ${skippedCount}`;
        }

        const planName = this.buildTestPlanName(isAnonymous, testPlanName);

        let message;
        const color = colorize[failed ? 'error' : 'success'];
        const executionStats = `PASSED: ${passed} FAILED: ${failed}${failedEvaluatingString} ABORTED: ${aborted}${skippedString} Duration: ${utils.getDuration(duration)}`;

        if (planName.trim() === '' || planName.trim() === 'Anonymous') {
            message = `Tests completed. ${executionStats} (Execution ID: ${executionId})`;
        } else {
            message = `Test plan${planName} completed ${executionStats} (${executionId})`;
        }

        this.printWorkerDivider();
        console.log(color(message));
        this.printWorkerDivider();

        this.printAllFailedTests(failedTests);
    }

    private buildTestPlanName(isAnonymous: boolean, testPlanName: string) {
        const suitesString = _.isEmpty(this.options.suites) ? '' : `Suite: ${this.options.suites}`;
        const labelsString = _.isEmpty(this.options.label) ? '' : `Label: ${this.options.label}`;
        const namesString = _.isEmpty(this.options.name) ? '' : `Name: ${this.options.name}`;
        const testIdString = _.isEmpty(this.options.testId) ? '' : `Test Id: ${this.options.testId}`;
        const name = [namesString, labelsString, suitesString, testIdString].filter(Boolean).join(', ');
        return isAnonymous ? ` anonymous (${name})` : ` '${testPlanName}'`;
    }

    public onTestPlanStarted(
        beforeTests: ExecutionList,
        tests: ExecutionList,
        afterTests: ExecutionList,
        testPlanName: string,
        executionId: string,
        isAnonymous: boolean,
        configName: string | null,
    ) {
        const writeTestList = (testList: TestWithCustomTestDataAndRunConfig[]) => {
            testList.forEach((test, index) => {
                const ds = test.testData?.index ? `- ${test.testData.index} / ${test.testData.total} Data set` : '';
                const testIdLabel = `(${test.testId})`;
                let suffix = '';

                if (test.testOptimization) {
                    suffix = ` [${test.testOptimization.reason}]`;
                } else if (utils.isQuarantineAndNotRemoteRun(test, this.options)) {
                    suffix = '-quarantine';
                }

                console.log('\t', index + 1, ':', `${test.name}${suffix}`, testIdLabel, ds);
            });
        };
        const configString = configName ? `config '${configName}'` : 'default configs';

        const sfdcCredentialLog = this.options.sfdcCredential ? ` SfdcCredential: ${this.options.sfdcCredential}` : '';
        const planStats = `${configString}, Project: ${this.options.project}, Branch: ${this.branchToUse}${sfdcCredentialLog}`;
        console.log(`Run${this.buildTestPlanName(isAnonymous, testPlanName)} test plan with ${planStats} (${executionId})`);
        this.printWorkerDivider();

        if (beforeTests.length > 0) {
            console.log('Before all:');
            writeTestList(beforeTests as TestWithCustomTestDataAndRunConfig[]);
        }

        console.log('Test list:');
        writeTestList(tests as TestWithCustomTestDataAndRunConfig[]);

        if (afterTests.length > 0) {
            console.log('After all:');
            writeTestList(afterTests as TestWithCustomTestDataAndRunConfig[]);
        }
        this.printWorkerDivider();
    }

    public onGetSlot(workerId: number, browser: string) {
        const gridNameOrId = this.options.grid || this.options.gridId;
        const instanceType = this.options.mode === CLI_MODE.APPIUM ? DEVICE : browser;
        if (gridNameOrId) {
            console.log(this.toWorkerIdPrefix(workerId), `Get ${chalk.underline(instanceType)} slot from ${chalk.underline(gridNameOrId)}`);
        }
    }

    public onGetSession(workerId: number, testName: string, mode: string) {
        const instanceType = mode === CLI_MODE.APPIUM ? DEVICE : 'browser';
        console.log(this.toWorkerIdPrefix(workerId), `Get ${instanceType} to run ${chalk.underline(testName)}`);
    }

    public onWaitToTestStart(workerId: number) {
        console.log(this.toWorkerIdPrefix(workerId), 'Wait for test start');
    }

    public onWaitToTestComplete(workerId: number) {
        console.log(this.toWorkerIdPrefix(workerId), 'Wait for test complete');
    }

    public onGetBrowserFailure(workerId: number, projectId: string, attempt: number) {
        if (attempt !== 2) {
            return; // we want to try 2 times before showing the message once
        }
        // heuristic, show the message on the same attempt
        const gridNameOrId = this.options.grid || this.options.gridId;
        const instanceType = this.options.mode === CLI_MODE.APPIUM ? 'device' : 'browser';
        if (gridNameOrId) { // if the user passes a grid or a gridId - show those
            console.log(colorize.warn(this.toWorkerIdPrefix(workerId), `It is taking us some time to get a ${instanceType} from the grid ${gridNameOrId}`));
        } else if (this.options.useLocalChromeDriver) {
            console.log(colorize.warn(this.toWorkerIdPrefix(workerId), 'We are having issues starting ChromeDriver for you locally'));
        } else if (this.options.host) {
            const gridType = this.options.mode === CLI_MODE.APPIUM ? 'Appium' : 'Selenium';
            console.log(colorize.warn(this.toWorkerIdPrefix(workerId), `We are having issues reaching your ${gridType} grid at ${this.options.host}:${this.options.port || 4444}`));
        } else {
            // in other cases - print nothing
        }
    }
}

export const Reporter = ConsoleReporter;
